import type { Pipeline } from "@/data/pipelines";

/**
 * The pipeline a deal belongs to, for showing its stages and resolving the stage code a save
 * sends.
 *
 * The deal record carries no pipeline id — only its stored stage key, which the backend writes as
 * the stage's `externalId` (or its id when it has none). Stage NAMES are not unique across
 * pipelines ("Closed Won" in both Sales and Renewals), so resolving by name picks whichever
 * pipeline lists it first, and a save then sends THAT pipeline's stage code — moving the deal out
 * of its own pipeline (and out of every pipeline view when the code doesn't match). So: an
 * explicit pipeline first, then the stored key, and the display name only for legacy rows that
 * stored a name.
 */
export function resolveDealPipeline<P extends Pipeline>(
  pipelines: P[],
  { pipelineId, storedStage, stageName }: {
    pipelineId?: string | null;
    storedStage?: string | null;
    stageName?: string | null;
  },
): P | undefined {
  if (pipelineId) return pipelines.find(p => p.id === pipelineId) || pipelines[0];
  if (storedStage) {
    const byKey =
      pipelines.find(p => p.stages.some(s => s.externalId === storedStage)) ||
      pipelines.find(p => p.stages.some(s => s.id === storedStage));
    if (byKey) return byKey;
  }
  if (stageName) {
    const byName = pipelines.find(p => p.stages.some(s => s.name === stageName));
    if (byName) return byName;
  }
  return pipelines[0];
}
